import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download } from "lucide-react";

const TEMPLATE = `slug,name,category_slug,price,compare_at_price,stock,sku,tags,materials,sizes,images,short_description,description,is_active
sample-ring,Sample Ring,rings,1999,2499,10,RIE-SMP-001,"featured,new",18k gold plated brass,"6,7,8",https://example.com/img1.jpg|https://example.com/img2.jpg,Minimal sample ring,Detailed description here,true`;

const AdminBulkUpload = () => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported · ${data.created} created, ${data.updated} updated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setLoading(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rieave-products-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-bulk-upload-page">
      <p className="font-label text-[11px] uppercase tracking-[0.22em] text-rose/70 mb-1">Catalog</p>
      <h1 className="font-hero text-3xl text-cream mb-2">Bulk product upload</h1>
      <p className="text-sm text-cream/75 mb-6 max-w-2xl">Upload a CSV to add or update products in bulk. Rows matching an existing slug are updated; new slugs are created.</p>

      <div className="rounded-2xl p-8" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex flex-col items-center justify-center text-center py-8 border border-dashed border-white/15 rounded-xl">
          <FileSpreadsheet size={40} className="text-rose/70 mb-3" />
          <p className="font-cormorant text-xl text-cream mb-1">Drop your CSV here</p>
          <p className="text-xs text-cream/65 mb-4">Columns: slug, name, category_slug, price, stock, sku, tags, sizes, images (pipe-separated), …</p>
          <label className="inline-flex items-center gap-2 cursor-pointer h-11 px-6 rounded-xl font-label text-[12px] uppercase tracking-[0.22em]"
            style={{ background: "linear-gradient(135deg, #C9789A, #9B8FBF)", color: "#fff" }}>
            <Upload size={14} /> {file ? file.name : "Choose file"}
            <input type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {file && (
            <button
              onClick={upload} disabled={loading}
              className="mt-3 h-10 px-6 rounded-xl font-label text-[11px] uppercase tracking-[0.22em] bg-white/5 text-cream hover:bg-white/10"
              data-testid="bulk-upload-button"
            >
              {loading ? "Importing…" : "Import now"}
            </button>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <button onClick={downloadTemplate} className="inline-flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-cream/60 hover:text-cream">
            <Download size={13} /> Download CSV template
          </button>
          <p className="text-[10px] uppercase tracking-[0.22em] text-cream/60">Max 5000 rows per upload</p>
        </div>

        {result && (
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl p-4 bg-white/5"><p className="font-hero text-2xl text-cream">{result.created}</p><p className="text-[10px] uppercase tracking-[0.22em] text-cream/65 mt-1">Created</p></div>
            <div className="rounded-xl p-4 bg-white/5"><p className="font-hero text-2xl text-cream">{result.updated}</p><p className="text-[10px] uppercase tracking-[0.22em] text-cream/65 mt-1">Updated</p></div>
            <div className="rounded-xl p-4 bg-white/5"><p className="font-hero text-2xl text-cream">{result.errors?.length || 0}</p><p className="text-[10px] uppercase tracking-[0.22em] text-cream/65 mt-1">Errors</p></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBulkUpload;
